import {
  Banner,
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { isResponseWithData } from 'lib-shared-auth';
import { AsyncStatus } from 'lib-shared-types-api';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { webformEndpointsApi } from 'apis/webformEndpoints';
import { HookFormEmailSelect, HookFormText } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { validateName } from 'utils/formValidator';
import { createAppModal } from 'utils/modal';

import styles from './createFormEndpointModal.module.scss';

type CreateFormEndpointFormFields = {
  emailIntegrationId: string[];
  description: string;
};

export const CreateFormEndpointModal = createAppModal(
  { type: 'settings:createFormEndpoint' },
  ({ isOpen, modal, hide }) => {
    const [getEmailAsyncStatus, setGetEmailAsyncStatus] =
      useState<AsyncStatus>();
    const form = useForm<CreateFormEndpointFormFields>({
      mode: 'all',
      defaultValues: {
        emailIntegrationId: [],
        description: '',
      },
    });

    const notify = useNotifications();
    const [createWebformEndpoint] =
      webformEndpointsApi.useCreateWebformEndpointMutation();

    const onCreateEndpointSubmit = async ({
      emailIntegrationId,
      description,
    }: CreateFormEndpointFormFields): Promise<void> => {
      const notifyError = (
        error = 'Error while creating the webform endpoint.',
      ) => {
        notify({ title: error }, NotificationType.Error);
      };

      try {
        const response = await createWebformEndpoint({
          description,
          email_integration_id: emailIntegrationId[0],
          form_integration_id: modal.arg.formId,
        }).unwrap();

        if (isResponseWithData(response)) {
          notify(
            {
              title: 'Success',
              text: `"${response.data?.description}" has been created.`,
              icon: 'i-done',
              time: 5000,
            },
            NotificationType.Success,
          );

          hide();
        } else {
          notifyError();
        }
      } catch {
        notifyError();
      }
    };

    const modalBody =
      getEmailAsyncStatus === 'rejected' ? (
        <Banner
          variant="fail"
          headline="Error"
          description={
            <Text.Paragraph>
              There was a problem getting the emails
            </Text.Paragraph>
          }
        />
      ) : (
        <FormGrid>
          <FormField>
            <HookFormText
              name="description"
              label="Endpoint name"
              control={form.control}
              rules={{
                required: 'Please add a name',
                validate: validateName,
              }}
              componentProps={{ placeholder: 'Assign a name to the endpoint' }}
            />
          </FormField>

          <FormField>
            <HookFormEmailSelect
              name="emailIntegrationId"
              control={form.control}
              label="Email"
              componentProps={{
                onEmailAsyncStatusChange: setGetEmailAsyncStatus,
              }}
            />
          </FormField>
        </FormGrid>
      );

    return (
      <Modal isOpen={isOpen} onClose={hide} className={styles.root}>
        <ModalHead title="Create webform endpoint" onClose={hide} />

        <ModalBody>{modalBody}</ModalBody>

        <ModalFoot
          confirmOnClick={form.handleSubmit(onCreateEndpointSubmit)}
          cancelOnClick={hide}
          cancelProps={{
            disabled: form.formState.isSubmitting,
          }}
          confirmProps={{
            isLoading:
              getEmailAsyncStatus === 'pending' || form.formState.isSubmitting,
            loadingIndicator:
              getEmailAsyncStatus === 'pending' ? 'Loading...' : 'Saving...',
            disabled:
              getEmailAsyncStatus === 'pending' ||
              !form.formState.isValid ||
              form.formState.isSubmitting ||
              !form.formState.isDirty,
          }}
        />
      </Modal>
    );
  },
);
